import clsx from 'clsx'
import React, { useCallback, useState } from 'react'
import styled from 'styled-components'

import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import MessageBanner from 'components/Luxkit/Banners/MessageBanner'
import TextButton from 'components/Luxkit/Button/TextButton'
import ModalBody from 'components/Luxkit/Modal/ModalBody'
import ModalContent from 'components/Luxkit/Modal/ModalContent'
import ModalFooter, {
  ModalFooterButtonBlocks,
} from 'components/Luxkit/Modal/ModalFooter'
import ModalHeader from 'components/Luxkit/Modal/ModalHeader'
import Group from 'components/utils/Group'
import noop from 'lib/function/noop'
import DateRangePicker from 'tripPlanner/components/TripModal/Common/DateRangePicker'

const HidingButton = styled(TextButton)`
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.2s ease-out;

  &.visible {
    opacity: 1;
    pointer-events: auto;
  }
`

interface Props {
  title?: string
  initialStartDate?: moment.Moment
  initialEndDate?: moment.Moment
  onChange: (startDate: moment.Moment, endDate: moment.Moment) => void
  onRemove?: () => void
  onCancel: () => void
  onBack?: () => void
  isSubmitting?: boolean
  errorMsg?: string
  onRequestClearError?: () => void
  doneLabel?: string
}

function MobileSelectTripDates(props: Props) {
  const {
    title = 'Add dates',
    initialStartDate,
    initialEndDate,
    onChange,
    onRemove,
    onCancel,
    onBack,
    isSubmitting,
    errorMsg,
    onRequestClearError = noop,
    doneLabel = 'Add dates',
  } = props

  const [startDate, setStartDate] = useState<moment.Moment | undefined>(
    initialStartDate,
  )
  const [endDate, setEndDate] = useState<moment.Moment | undefined>(
    initialEndDate,
  )

  const [showNoDatesError, setShowNoDatesError] = useState(false)

  const errorToShow =
    errorMsg || (showNoDatesError ? 'Please select dates' : undefined)

  const onUpdate = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault()
      if (startDate) {
        setShowNoDatesError(false)
        onChange(startDate, endDate ?? startDate)
      } else {
        setShowNoDatesError(true)
      }
    },
    [onChange, startDate, endDate],
  )

  const onClickRemove = useCallback(() => {
    setShowNoDatesError(false)
    onRemove?.()
  }, [onRemove])

  const onChangeStartDate = useCallback(
    (date: moment.Moment | undefined) => {
      setStartDate(date)
      onRequestClearError()
    },
    [setStartDate, onRequestClearError],
  )

  const onChangeEndDate = useCallback(
    (date: moment.Moment | undefined) => {
      setEndDate(date)
      onRequestClearError()
    },
    [setEndDate, onRequestClearError],
  )

  return (
    <form onSubmit={onUpdate}>
      <ModalHeader
        title={title}
        onBackButtonClick={onBack}
        onCloseButtonClick={onCancel}
      />
      <ModalBody>
        <ModalContent>
          <VerticalSpacer gap={16}>
            <DateRangePicker
              onChangeStartDate={onChangeStartDate}
              onChangeEndDate={onChangeEndDate}
              startDate={startDate}
              endDate={endDate}
              allowSameDay
            />
            {errorToShow && (
              <MessageBanner kind="critical" description={errorToShow} />
            )}
          </VerticalSpacer>
        </ModalContent>
      </ModalBody>
      <ModalFooter>
        <Group
          direction="vertical"
          gap={8}
          tabletDirection="horizontal-reverse"
          tabletHorizontalAlign="space-between"
        >
          <ModalFooterButtonBlocks>
            <TextButton
              kind="primary"
              size="large"
              type="submit"
              disabled={isSubmitting}
            >
              {doneLabel}
            </TextButton>
            <TextButton
              kind="tertiary"
              size="large"
              onClick={onCancel}
              disabled={isSubmitting}
            >
              Cancel
            </TextButton>
          </ModalFooterButtonBlocks>
          {onRemove && (
            <HidingButton
              kind="tertiary"
              size="large"
              onClick={onClickRemove}
              className={clsx({ visible: !!startDate })}
            >
              Clear dates
            </HidingButton>
          )}
        </Group>
      </ModalFooter>
    </form>
  )
}

export default MobileSelectTripDates
